import type { CueId } from '@sol-pool/engine-physics'
import type { Pontos } from './escala'

/**
 * As raridades, da mais fraca à mais rara. A ordem importa: é a ordem do
 * orçamento e a ordem de exibição.
 *
 * `gratuito` é a PRIMEIRA, e é a única que não se cunha: é a raridade do taco
 * que vem junto com a carteira. Ela existe porque o taco de graça precisa ser
 * PIOR que o comum cunhado — se o item mais barato da coleção empatasse com o
 * que todo mundo já tem, o mint não teria nada para vender no primeiro
 * degrau. Ver `RARIDADES_CUNHAVEIS` para quem NÃO pode recebê-la.
 */
export type Raridade = 'gratuito' | 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary'
export const RARIDADES = ['gratuito', 'common', 'uncommon', 'rare', 'epic', 'legendary'] as const satisfies readonly Raridade[]
export const raridadeValida = (v: unknown): v is Raridade =>
  typeof v === 'string' && (RARIDADES as readonly string[]).includes(v)

/**
 * As raridades que um NFT pode ter.
 *
 * `gratuito` fica de fora: o sorteio do mint nunca a produz, a posse de teste
 * não a inventa e a leitura da chain a recusa (`lerPerfilPadrao`). Um NFT
 * gratuito seria um item impossível — 195 pontos e sem loja de upgrade — e a
 * lista existe para que quem itera raridades para CUNHAR não precise lembrar
 * de excluí-la na mão.
 */
export type RaridadeCunhavel = Exclude<Raridade, 'gratuito'>
export const RARIDADES_CUNHAVEIS = ['common', 'uncommon', 'rare', 'epic', 'legendary'] as const satisfies readonly RaridadeCunhavel[]

/** O arquétipo é o id do catálogo da física: é ele que dá a arte e o código no replay. */
export type Arquetipo = CueId

/**
 * O que um taco É, antes de virar números.
 *
 * É isto que vai gravado no NFT (imutável) e é disto que os gratuitos são
 * feitos. Os atributos saem daqui pelo ruleset; nunca o contrário.
 */
export type PerfilDeTaco = {
  raridade: Raridade
  arquetipo: Arquetipo
  /** Inteiro de 32 bits. Zero é "sem variação" — a seed dos gratuitos. */
  seed: number
  /** Temporada/lote de cunhagem. Só informativo por enquanto. */
  geracao: number
}

/**
 * As regras de equilíbrio, versionadas.
 *
 * Mudar um número aqui muda TODO taco novo — e nenhum replay antigo: a partida
 * grava os atributos resultantes, não a versão. É por isso que o ruleset pode
 * ser rebalanceado sem maquinaria de época no replay.
 *
 * O que ele MOVE são os tacos já cunhados nas partidas FUTURAS, porque os
 * atributos são derivados do perfil on-chain a cada partida. Um rebalanceio é
 * anunciado, não silencioso.
 *
 * As versões anteriores ficam em `rulesets-congelados.ts`, com a `mira` que
 * elas tinham. Nada daqui as importa.
 */
export type Ruleset = {
  versao: number
  /** Soma dos quatro atributos, em pontos, por raridade. */
  orcamento: Record<Raridade, number>
  /** A DIREÇÃO de cada arquétipo: pesos relativos, distribuídos pelo orçamento. */
  perfil: Record<Arquetipo, Pontos>
  /** Quantos pontos a seed pode mover entre atributos. */
  amplitudeDaSeed: number
  /**
   * Quanto UM nível comprado vale, em pontos, no atributo escolhido.
   *
   * Os níveis são configuração versionada como o resto: o taco guarda o
   * NÍVEL (o que foi pago), e o ruleset de hoje diz quanto ele rende. A
   * partida grava os atributos resultantes, não a versão — rebalancear isto
   * não retroage em nenhum replay nem exige migrar linha de banco.
   */
  pontosPorNivel: number
  /** Teto de níveis por atributo — o pico que um upgrade pode ter. */
  nivelMaximo: number
  /** Teto de pontos por atributo, o mesmo da escala visível (0..100). */
  tetoPorAtributo: number
  /** Quantos níveis, no total, um taco daquela raridade pode comprar. */
  orcamentoDeNiveis: Record<Raridade, number>
}

/**
 * V3. A mira saiu do NFT, e os números foram re-ancorados por causa disso.
 *
 * O ORÇAMENTO COMUM é a soma do taco padrão, como sempre foi — só que o padrão
 * sem a mira vale 140 (50 + 40 + 50 + 0) em vez de 207. A regra que gerou os
 * números antigos é preservada: o padrão não se move, o degrau entre as
 * raridades pagas é 15, e o gratuito fica 12 abaixo do comum (menos que um
 * degrau, porque ele é o piso e não "mais uma raridade abaixo").
 *
 * CONSEQUÊNCIA QUE PRECISA SER DITA: com orçamento único por raridade, todos
 * os arquétipos passam a somar o mesmo, e cada um gastava uma fatia diferente
 * na mira (pesado 46, padrão 67, efeito 49, preciso 77). Comparado ao v2, nos
 * quatro atributos que ficaram: o padrão não muda, o `pesado` perde 21 pontos
 * de soma, o `efeito` 18, e o `preciso` ganha 10. Preservar as somas de todos
 * exigiria orçamento por ARQUÉTIPO — um campo novo no tipo, para corrigir algo
 * que os testes de dominância já garantem não ser desequilíbrio.
 *
 * `pesado.peso` VOLTA A 100. Os 2 pontos saíram em 2026-09-13 por um motivo
 * só: a mira efetiva engrossava com o excesso de massa e, medido por ela, o
 * pesado era quem tinha menos direções para mirar. O motivo deixou de existir.
 *
 * `preciso` É OUTRO TACO. A identidade dele era a mira — "preciso tem de ter a
 * mira como o atributo mais alto". Sem ela, o perfil antigo (peso 0, efeito 38,
 * aderência 50) ficava com dois eixos não-zero, toda a verba se concentrava
 * neles e a aderência estourava o teto no legendary, reprovando "legendary não
 * encosta no máximo". Agora ele é o taco de CONTROLE: peso 30 lhe dá um
 * terceiro eixo, e a aderência lidera em toda raridade como a mira liderava.
 * O id `preciso` NÃO muda — está gravado on-chain nas NFTs já cunhadas, e é
 * por ele que `lerPerfilPadrao` as reconhece. Só o rótulo visível mudou.
 *
 * Os números saíram de uma varredura de combinações (perfil do `preciso` ×
 * degrau × distância do gratuito × peso do pesado) validada contra todos os
 * invariantes do pacote: 438 passaram, e esta é a de maior folga — 8 pontos
 * até o teto no legendary, 12 de margem de dominância de fábrica e 5 no teto
 * de poder com níveis.
 */
export const RULESET_V3: Ruleset = {
  versao: 3,
  orcamento: { gratuito: 128, common: 140, uncommon: 155, rare: 170, epic: 185, legendary: 200 },
  perfil: {
    padrao: { peso: 50, efeito: 40, aderencia: 50, quebra: 0 },
    pesado: { peso: 100, efeito: 20, aderencia: 25, quebra: 100 },
    preciso: { peso: 30, efeito: 40, aderencia: 60, quebra: 0 },
    efeito: { peso: 33, efeito: 100, aderencia: 100, quebra: 0 },
  },
  amplitudeDaSeed: 3,
  pontosPorNivel: 2,
  nivelMaximo: 5,
  tetoPorAtributo: 100,
  // Menos níveis quanto mais rara: a raridade já vem com mais pontos base, e
  // o orçamento de upgrade é o que impede a lendária de somar o mesmo tanto
  // por cima e abrir uma distância que a comum nunca alcança.
  // `gratuito: 0` — o taco de graça não é de ninguém e não evolui. O campo
  // existe porque o tipo cobre TODA raridade; quem o lê (`podeSubir`) recusa
  // o primeiro nível por `orcamento`, e `tacoGratuito` nem monta loja.
  orcamentoDeNiveis: { gratuito: 0, common: 10, uncommon: 9, rare: 8, epic: 7, legendary: 6 },
}
